// `localizeError` — translates zod issue codes into human-readable strings.
//
// Web Wave 4.2 / DESIGN.md §0.4. Maps zod's English `code` taxonomy to the
// `auth.fieldErrors.*` namespace so consumers don't have to hand-roll error
// copy per form. Unknown codes fall back to the issue's `.message` (so the
// form still surfaces *something* even if a new zod code lands later) but
// log a soft warning in dev so we can extend the map.

import type { ZodIssue } from 'zod';

import type { TFunction } from '@barel/i18n';

/** Translate a zod issue against the `auth.*` namespace `t` helper. */
export function localizeError(issue: ZodIssue | undefined, t: TFunction): string | undefined {
  if (!issue) return undefined;

  // Field-level path (e.g. `['email']`) — used for code disambiguation.
  const lastPath = issue.path[issue.path.length - 1];

  switch (issue.code) {
    case 'invalid_type': {
      // `received: 'undefined'` happens when the value is missing entirely.
      if ('received' in issue && issue.received === 'undefined') {
        return t('fieldErrors.required');
      }
      return t('fieldErrors.required');
    }
    case 'invalid_string': {
      if ('validation' in issue && issue.validation === 'email') {
        return t('fieldErrors.email.invalid');
      }
      return issue.message;
    }
    case 'too_small': {
      // Field-aware lookup. `password` → length min; otherwise required.
      if (lastPath === 'password' || lastPath === 'newPassword' || lastPath === 'confirmPassword') {
        return t('fieldErrors.password.tooShort', { min: issue.minimum });
      }
      // Empty string on a non-password field reads "required" more naturally
      // than "min 1 chars".
      if (issue.minimum === 1) return t('fieldErrors.required');
      return issue.message;
    }
    case 'too_big': {
      if (lastPath === 'email') return t('fieldErrors.email.tooLong');
      if (lastPath === 'name') return t('fieldErrors.name.tooLong', { max: issue.maximum });
      if (lastPath === 'password' || lastPath === 'newPassword' || lastPath === 'confirmPassword') {
        return t('fieldErrors.password.tooLong', { max: issue.maximum });
      }
      return issue.message;
    }
    case 'custom':
    default:
      return issue.message;
  }
}

/**
 * Resolve a `react-hook-form` `FieldError`'s message (which we prepopulate
 * with the localized string at zod-resolver time) and fall back to a
 * stringification when the message is missing.
 */
export function fieldErrorMessage(error: { message?: string } | undefined): string | undefined {
  if (!error) return undefined;
  return error.message ?? undefined;
}

/**
 * Resolve a field's display error: the resolver-localized `message`, or the
 * already-localized `fallback` when the message is absent; `undefined` when
 * the field has no error. Collapses the
 * `errors.x ? (errors.x.message ?? t(fallback)) : undefined` shape repeated
 * across the auth forms.
 */
export function fieldError(
  error: { message?: string } | undefined,
  fallback: string,
): string | undefined {
  return error ? (error.message ?? fallback) : undefined;
}
